import React, { useState } from 'react';
import { Download, CheckCircle } from 'lucide-react';
import Papa from 'papaparse';
import InfoBox from './components/InfoBox';
import UploadDropzone from './components/UploadDropzone';
import StatsCards from './components/StatsCards';
import ValidationErrors from './components/ValidationErrors';
import DataPreview from './components/DataPreview';
import { uploadUsers, uploadContent, uploadAssignments, downloadTemplate, downloadSample } from '@/services/PagesServices/BulkUploadService';
import './BulkUploader.css';

interface ValidationError {
  row: number;
  field: string;
  error: string;
}

interface PreviewData {
  [key: string]: string;
}

type TabType = 'users' | 'content' | 'assignments';

interface TabConfig {
  title: string;
  subtitle: string;
  requirements: {
    required: string;
    optional: string;
    additional?: string;
  };
  previewColumns: string[];
}

interface UploadStats {
  totalRows: number;
  valid: number;
  errors: number;
  warnings: number;
  duplicates: number;
}

const BulkUploadPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [file, setFile] = useState<File | null>(null);
  const [stats, setStats] = useState<UploadStats | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tabConfigs: Record<TabType, TabConfig> = {
    users: {
      title: 'Bulk Upload Users',
      subtitle: 'Upload multiple users using CSV file',
      requirements: {
        required: 'First Name, Last Name, Email, Department, Job Title',
        optional: 'Manager Email, Phone, Location',
        additional: 'Maximum 1000 rows per upload'
      },
      previewColumns: ['NAME', 'EMAIL', 'DEPARTMENT', 'JOB TITLE']
    },
    content: {
      title: 'Bulk Update Content',
      subtitle: 'Update content metadata in bulk using CSV',
      requirements: {
        required: 'Content ID, Field to Update, New Value',
        optional: 'Title, Description, Category, Tags, Status, Owner',
        additional: 'Changes will overwrite existing values'
      },
      previewColumns: ['CONTENT ID', 'FIELD', 'NEW VALUE', 'CATEGORY']
    },
    assignments: {
      title: 'Bulk Upload Assignments / Training',
      subtitle: 'Assign courses to multiple users via CSV',
      requirements: {
        required: 'User Email, Course ID or Name',
        optional: 'Due Date, Send Notification (Yes/No)',
        additional: 'Users must exist in the system'
      },
      previewColumns: ['USER EMAIL', 'COURSE', 'DUE DATE', 'NOTIFICATION']
    }
  };

  const currentConfig = tabConfigs[activeTab];

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);

    // Parse CSV file
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as PreviewData[];

        if (data.length === 0) {
          setError('CSV file is empty');
          return;
        }

        // Set basic stats
        setStats({
          totalRows: data.length,
          valid: data.length,
          errors: 0,
          warnings: 0,
          duplicates: 0
        });

        // Clear validation errors for now (server will validate)
        setValidationErrors([]);

        // Set preview data (first 5 rows)
        setPreviewData(data.slice(0, 5));
      },
      error: (error) => {
        setError(`Failed to parse CSV: ${error.message}`);
        setStats(null);
        setValidationErrors([]);
        setPreviewData([]);
      }
    });
  };

  const handleCancel = () => {
    setFile(null);
    setStats(null);
    setValidationErrors([]);
    setPreviewData([]);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    // Reset state when changing tabs
    handleCancel();
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate(activeTab);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDownloadSample = async () => {
    try {
      await downloadSample(activeTab);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleConfirmUpload = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      let result;
      if (activeTab === 'users') {
        result = await uploadUsers(file);
      } else if (activeTab === 'content') {
        result = await uploadContent(file);
      } else {
        result = await uploadAssignments(file);
      }

      // Update stats with server response
      setStats({
        totalRows: result.data.totalRows,
        valid: result.data.validRows,
        errors: result.data.errorRows,
        warnings: result.data.warnings.length,
        duplicates: 0
      });

      // Update validation errors
      setValidationErrors(result.data.errors.map((err: any) => ({
        row: err.row,
        field: err.field,
        error: err.message
      })));

      if (result.success) {
        alert(`Upload completed! ${result.data.inserted || result.data.updated} records processed.`);
        setFile(null);
        setPreviewData([]);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-bulk-upload-container">
      <div className="admin-bulk-upload-breadcrumb">
        <span className="admin-bulk-upload-breadcrumb-item">Tools</span>
        <span className="admin-bulk-upload-breadcrumb-separator">&gt;</span>
        <span className="admin-bulk-upload-breadcrumb-item">Bulk Uploader</span>
      </div>

      <h1 className="admin-bulk-upload-title">Tools</h1>
      <p className="admin-bulk-upload-subtitle">Administrative utilities and system tools</p>

      <div className="admin-bulk-upload-tabs">
        <button 
          className={`admin-bulk-upload-tab ${activeTab === 'users' ? 'admin-bulk-upload-tab-active' : ''}`}
          onClick={() => handleTabChange('users')}
        >
          Upload Users
        </button>
        <button 
          className={`admin-bulk-upload-tab ${activeTab === 'content' ? 'admin-bulk-upload-tab-active' : ''}`}
          onClick={() => handleTabChange('content')}
        >
          Upload Content
        </button>
        <button 
          className={`admin-bulk-upload-tab ${activeTab === 'assignments' ? 'admin-bulk-upload-tab-active' : ''}`}
          onClick={() => handleTabChange('assignments')}
        >
          Upload Assignments / Training
        </button>
      </div>

      <div className="admin-bulk-upload-header">
        <div>
          <h2 className="admin-bulk-upload-section-title">{currentConfig.title}</h2>
          <p className="admin-bulk-upload-section-subtitle">{currentConfig.subtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="admin-bulk-upload-download-btn" onClick={handleDownloadTemplate}>
            <Download size={16} />
            Download Template
          </button>
          <button
            className="admin-bulk-upload-download-btn"
            onClick={handleDownloadSample}
            style={{ backgroundColor: '#059669' }}
          >
            <Download size={16} />
            Download Sample
          </button>
        </div>
      </div>

      <InfoBox requirements={currentConfig.requirements} />

      {error && (
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: '#c33'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {!file ? (
        <UploadDropzone onFileSelect={handleFileSelect} />
      ) : (
        <>
          {stats && <StatsCards stats={stats} />}

          <ValidationErrors errors={validationErrors} />

          <DataPreview data={previewData} columns={currentConfig.previewColumns} />

          <div className="admin-bulk-upload-actions">
            <button
              className="admin-bulk-upload-cancel-btn"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="admin-bulk-upload-confirm-btn"
              onClick={handleConfirmUpload}
              disabled={loading}
            >
              <CheckCircle size={16} />
              {loading ? 'Uploading...' : 'Confirm & Upload'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BulkUploadPage;